import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchFromStrapi } from "../api/strapi";
import { SkeletonBlock } from "./SkeletonUI";
import DOMPurify from 'dompurify';


/* ─── Skeleton ───────────────────────────────────────────────────── */
function ServicesSkeleton() {
  return (
    <section className="svc-section">
      <div className="container">
        <div className="svc-header">
          <div>
            <SkeletonBlock width={90}  height={10} style={{ marginBottom: 12 }} />
            <SkeletonBlock width={200} height={30} />
          </div>
        </div>
        <div className="svc-scroll">
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ flex: "0 0 300px" }}>
              <SkeletonBlock height={380} radius={14} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Single poster card ─────────────────────────────────────────── */
function ServiceCard({ item }) {
  const imageUrl  = item.attributes?.Image?.data?.attributes?.url;
  const category  = item.attributes?.Category    || "";
  const title     = item.attributes?.Title        || "";
  const description = item.attributes?.Description || "";
  const sanitizedHtmlDescription = DOMPurify.sanitize(description || "");


  return (
    <Link to={`/services/${item.id}`} className="svc-card" aria-label={title}>
      {imageUrl && <img src={imageUrl} alt={title} loading="lazy" />}

      <div className="svc-card__overlay" />

      {category && <span className="svc-card__tag">{category}</span>}

      <div className="svc-card__content">
        {description && <span className="svc-card__desc"><div dangerouslySetInnerHTML={{ __html: sanitizedHtmlDescription }} /></span>}
        <h3 className="svc-card__title">{title}</h3>
        <span className="svc-card__cta">
          Learn more
        </span>
      </div>
    </Link>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const sectionRef              = useRef(null);

  useEffect(() => {
    fetchFromStrapi("/ecl-services-sections")
      .then(res => setServices(res?.data?.data || res?.data || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  /* Scroll reveal — fires once when section enters viewport */
  useEffect(() => {
    if (loading || !services.length) return;
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.querySelectorAll(".scroll-fade-up").forEach(el =>
            el.classList.add("is-visible")
          );
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [loading, services]);

  if (loading) return <ServicesSkeleton />;
  if (!services.length) return null;

  return (
    <section className="svc-section" ref={sectionRef}>
      <div className="container">
        <div className="svc-header scroll-fade-up" style={{ "--reveal-delay": "0s" }}>
          <div>
            <p
              className="text-uppercase fw-semibold mb-1"
              style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}
            >
              What we do
            </p>
            <h2
              className="fw-bold mb-0"
              style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", color: "#0a1a3a" }}
            >
              Our Services
            </h2>
          </div>

          <Link to="/services" className="svc-view-all">
            View all services
          </Link>
        </div>

        <div className="svc-scroll">
          {services.map((item, i) => (
            <div
              key={item.id}
              className="svc-card-wrapper scroll-fade-up"
              style={{ "--reveal-delay": `${0.1 + i * 0.07}s` }}
            >
              <ServiceCard item={item} />
            </div>
          ))}
        </div>

        {/* Scroll hint — only on touch devices */}
        <p className="svc-scroll-hint scroll-fade-up" style={{ "--reveal-delay": "0.5s" }}>
          <i className="bi bi-arrow-left-right me-1" /> Swipe to explore
        </p>
      </div>
    </section>
  );
};

export default Services;
